import { Layers, Sparkles } from "lucide-react";
import { cn } from "@/modern/utils";
import { ANALYSING_LOADER_VARIANTS } from "./wizardStepDisplay";

const LOADER_VISUALS = {
  questions: {
    icon: Sparkles,
    lineWidths: [100, 88, 94, 72, 58, 40],
  },
  context: {
    icon: Layers,
    lineWidths: [72, 100, 84, 96, 68, 52],
  },
} as const;

interface AnalysingLoaderProps {
  variant: "questions" | "context";
}

const WaitingVisual = ({
  icon: Icon,
  lineWidths,
}: {
  icon: typeof Sparkles;
  lineWidths: readonly number[];
}) => (
  <div className="flex flex-col items-center gap-6">
    <div className="relative flex h-14 w-14 items-center justify-center">
      <div
        className="absolute inset-0 rounded-2xl bg-primary/20 context-loader-ring"
        aria-hidden
      />
      <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary context-loader-breathe">
        <Icon className="h-6 w-6" />
      </div>
    </div>

    <div
      className="relative w-56 sm:w-64 rounded-xl bg-muted/25 overflow-hidden px-5 py-5"
      aria-hidden
    >
      <div className="space-y-2.5">
        {lineWidths.map((width, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 rounded-full bg-muted-foreground/20",
              i % 2 === 0 ? "context-loader-line-a" : "context-loader-line-b"
            )}
            style={{ width: `${width}%`, animationDelay: `${i * 0.35}s` }}
          />
        ))}
      </div>
      <div className="pointer-events-none absolute inset-x-0 h-10 bg-gradient-to-b from-transparent via-primary/15 to-transparent context-loader-scan" />
    </div>
  </div>
);

export const AnalysingLoader = ({ variant }: AnalysingLoaderProps) => {
  const { loaderTitle, loaderSubtitle } = ANALYSING_LOADER_VARIANTS[variant];
  const { icon, lineWidths } = LOADER_VISUALS[variant];

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="shrink-0 px-8 py-3.5 border-b border-border/40">
        <h2 className="text-base font-semibold text-foreground">{loaderTitle}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground max-w-xl">{loaderSubtitle}</p>
      </div>

      <div className="flex flex-1 min-h-0 items-center justify-center px-8 py-6 overflow-y-auto">
        <div className="w-full max-w-3xl mx-auto">
          <div className="flex flex-col items-center text-center px-4 py-4 sm:px-8 sm:py-6">
            <WaitingVisual icon={icon} lineWidths={lineWidths} />
          </div>
        </div>
      </div>
    </div>
  );
};
